import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCafeAuth } from "../auth/CafeAuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { CafeShell, Field, inputStyle } from "./CafeShell";

const PARTNER_COPY = {
  creator: {
    heading: "Join as a creator",
    blurb: "Two minutes. You get a personalized link your audience can scan or click — and 44% of every $5.55 reading they unlock through you.",
    namePlaceholder: "Your name or handle",
    nameLabel: "Your name",
    slugHint: "your-handle",
    notePlaceholder: "A line your readers will see when they land — keep it warm, keep it you.",
    submitIdle: "Create my creator page",
    submitWorking: "Setting up…",
  },
  cafe: {
    heading: "Join as a cafe",
    blurb: "Two minutes. You get a custom landing page, a printable table-tent QR, and 44% of every reading scanned from your spot.",
    namePlaceholder: "Flora Cafe",
    nameLabel: "Cafe name",
    slugHint: "flora",
    notePlaceholder: "From the team at Flora — enjoy a moment with us.",
    submitIdle: "Create my cafe page",
    submitWorking: "Setting up…",
  },
};

export default function CafeSignupPage() {
  const { signup } = useCafeAuth();
  const navigate = useNavigate();
  const [partnerType, setPartnerType] = useState("creator");
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    slug: "",
    custom_note: "",
    payout_method: "venmo",
    payout_handle: "",
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const copy = PARTNER_COPY[partnerType];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signup({
        ...form,
        slug: form.slug.toLowerCase().trim(),
        custom_note: form.custom_note.trim() || null,
        partner_type: partnerType,
      });
      navigate("/cafe/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const TypeBtn = ({ value, children }) => (
    <button
      type="button"
      data-testid={`signup-type-${value}`}
      onClick={() => setPartnerType(value)}
      className="toggle-btn"
      style={{
        flex: 1,
        background: partnerType === value ? "var(--cream)" : "transparent",
        color: partnerType === value ? "var(--bg-1)" : "var(--cream-soft)",
        borderColor: partnerType === value ? "var(--cream)" : "rgba(201,168,119,0.35)",
      }}
    >
      {children}
    </button>
  );

  return (
    <CafeShell testId="cafe-signup-page">
      <h2 className="italic-quote" style={{ fontSize: 28, color: "var(--cream)", textAlign: "center", margin: "0 0 8px" }}>
        {copy.heading}
      </h2>
      <p className="body-copy" style={{ textAlign: "center", marginBottom: 28, fontSize: 14 }}>
        {copy.blurb}
      </p>

      <Field label="Which one are you?">
        <div style={{ display: "flex", gap: 10 }} data-testid="signup-type-picker">
          <TypeBtn value="creator">Creator / Individual</TypeBtn>
          <TypeBtn value="cafe">Cafe / Coffee Trailer</TypeBtn>
        </div>
      </Field>

      <form onSubmit={handleSubmit}>
        <Field label={copy.nameLabel}>
          <input data-testid="signup-name" type="text" required value={form.name} onChange={set("name")} style={inputStyle} placeholder={copy.namePlaceholder} />
        </Field>
        <Field
          label="Your slug"
          hint={`Your link will be ${window.location.origin}/c/${form.slug.toLowerCase().trim() || copy.slugHint}`}
        >
          <input
            data-testid="signup-slug"
            type="text"
            required
            value={form.slug}
            onChange={set("slug")}
            style={inputStyle}
            placeholder={copy.slugHint}
            pattern="^[a-z0-9][a-z0-9\-]{1,38}[a-z0-9]$"
          />
        </Field>
        <Field label="Custom note (optional, shown on your landing)">
          <textarea
            data-testid="signup-note"
            value={form.custom_note}
            onChange={set("custom_note")}
            style={{ ...inputStyle, minHeight: 70, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 16 }}
            placeholder={copy.notePlaceholder}
            maxLength={160}
          />
        </Field>
        <Field label="Email">
          <input data-testid="signup-email" type="email" required value={form.email} onChange={set("email")} style={inputStyle} placeholder={partnerType === "creator" ? "you@gmail.com" : "hello@yourcafe.com"} />
        </Field>
        <Field label="Password" hint="Min. 6 characters.">
          <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={set("password")} style={inputStyle} />
        </Field>
        <Field label="Payout method">
          <div style={{ display: "flex", gap: 10 }}>
            {["venmo", "stripe"].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`signup-payout-${m}`}
                onClick={() => setForm({ ...form, payout_method: m })}
                className="toggle-btn"
                style={{
                  flex: 1,
                  background: form.payout_method === m ? "var(--cream)" : "transparent",
                  color: form.payout_method === m ? "var(--bg-1)" : "var(--cream-soft)",
                  borderColor: form.payout_method === m ? "var(--cream)" : "rgba(201,168,119,0.35)",
                }}
              >
                {m === "venmo" ? "Venmo" : "Stripe / Bank"}
              </button>
            ))}
          </div>
        </Field>
        <Field label={form.payout_method === "venmo" ? "Venmo handle" : "Stripe email / bank handle"}>
          <input
            data-testid="signup-payout-handle"
            type="text"
            required
            value={form.payout_handle}
            onChange={set("payout_handle")}
            style={inputStyle}
            placeholder={form.payout_method === "venmo" ? "@yourhandle" : "payouts@you.com"}
          />
        </Field>
        {error && <p data-testid="signup-error" className="error-banner" style={{ marginBottom: 16 }}>{error}</p>}
        <button data-testid="signup-submit" className="primary-btn" disabled={submitting} type="submit">
          {submitting ? copy.submitWorking : copy.submitIdle}
        </button>
        <p className="body-copy" style={{ textAlign: "center", marginTop: 18, fontSize: 13 }}>
          Already set up? <Link to="/cafe/login" data-testid="signup-to-login" style={{ color: "var(--gold)", textDecoration: "underline" }}>Sign in</Link>
        </p>
        <p className="body-copy" style={{ textAlign: "center", marginTop: 10, fontSize: 13 }}>
          Want the full story first? <Link to="/begin" data-testid="signup-to-begin" style={{ color: "var(--gold)", textDecoration: "underline" }}>Apply for the Beta ✦</Link>
        </p>
      </form>
    </CafeShell>
  );
}
